/**
 * Real-time Data for Hygea Chatbot
 */

import { API_BASE } from '../config/runtime';

// Cache system
let dataCache = {
  services: null,
  branches: null,
  promos: null,
  therapists: null,
  lastFetch: {},
};

const CACHE_DURATION = 5 * 60 * 1000;
const EN_DASH = '\u2013';
const PESO_SYMBOL = '\u20b1';

const isCacheValid = (key) => {
  const lastFetch = dataCache.lastFetch[key] || 0;
  return Date.now() - lastFetch < CACHE_DURATION;
};

// ===============================
// FETCH FUNCTIONS
// ===============================

export const fetchServices = async () => {
  try {
    if (isCacheValid('services') && dataCache.services) {
      return dataCache.services;
    }

    const res = await fetch(`${API_BASE}/get_services.php`);
    const data = await res.json();

    if (data.success && data.services) {
      dataCache.services = data.services;
      dataCache.lastFetch.services = Date.now();
      return data.services;
    }

    return [];
  } catch (err) {
    console.error(err);
    return [];
  }
};

export const fetchBranches = async () => {
  try {
    if (isCacheValid('branches') && dataCache.branches) {
      return dataCache.branches;
    }

    const res = await fetch(`${API_BASE}/get_branches.php`);
    const data = await res.json();

    if (data.success && data.branches) {
      dataCache.branches = data.branches;
      dataCache.lastFetch.branches = Date.now();
      return data.branches;
    }

    return [];
  } catch (err) {
    console.error(err);
    return [];
  }
};

export const fetchPromos = async () => {
  try {
    if (isCacheValid('promos') && dataCache.promos) {
      return dataCache.promos;
    }

    const res = await fetch(`${API_BASE}/get_promos.php`);
    const data = await res.json();

    if (data.success && data.promotions) {
      dataCache.promos = data.promotions;
      dataCache.lastFetch.promos = Date.now();
      return data.promotions;
    }

    return [];
  } catch (err) {
    console.error(err);
    return [];
  }
};

export const fetchTherapists = async () => {
  try {
    if (isCacheValid('therapists') && dataCache.therapists) {
      return dataCache.therapists;
    }

    const res = await fetch(`${API_BASE}/get_therapists.php`);
    const data = await res.json();

    if (data.success && data.therapists) {
      dataCache.therapists = data.therapists;
      dataCache.lastFetch.therapists = Date.now();
      return data.therapists;
    }

    return [];
  } catch (err) {
    console.error(err);
    return [];
  }
};

// ===============================
// STATIC DATA
// ===============================

export const staticChatbotData = {
  faqs: [
    {
      question: 'How do I book an appointment?',
      answer: 'You can book appointments by visiting the Treatments section, selecting a service, choosing your preferred date and time, and completing the booking. You\'ll receive instant confirmation.',
    },
    {
      question: 'Can I cancel or reschedule my appointment?',
      answer: 'Yes! You can cancel or reschedule up to 24 hours before your appointment. Visit History and select your appointment to modify or cancel it.',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept credit/debit cards, e-wallets (GCash, PayMaya), and bank transfers. Choose your preferred method during checkout.',
    },
    {
      question: 'How can I track my bookings?',
      answer: 'Visit the History section to view all your bookings and download receipts.',
    },
    {
      question: 'How do I reset my password?',
      answer: 'Click "Forgot Password", enter your email, and follow the reset link sent to your inbox.',
    },
    {
      question: 'Can I modify my notification settings?',
      answer: 'Yes! Go to Settings to customize your notification preferences.',
    },
  ],

  navigationGuides: [
    { page: 'Home', description: 'Browse services and promotions' },
    { page: 'Services', description: 'View wellness service catalog' },
    { page: 'Therapists', description: 'Meet our practitioners' },
    { page: 'Treatments', description: 'Book appointments' },
    { page: 'History', description: 'View booking history' },
    { page: 'Profile', description: 'Manage account' },
    { page: 'Settings', description: 'Preferences and security' },
  ],

  responses: {
    greeting: {
      keywords: ['hello', 'hi', 'hey'],
      response: 'Hello! Welcome to Hygea. How can I assist you?',
    },
    help: {
      keywords: ['help', 'support'],
      response: 'I can help with services, bookings, branches, FAQs, and system navigation. What would you like to know?',
    },
    defaultFallback:
      'I\'m not sure I understood that. Try asking about services, bookings, branches, or our features. You can also request to speak with a real agent!',
  },
};

export const STORE_HOURS_TITLE = 'Hours of Operation:';
export const STORE_HOURS_LINES = [
  `Monday ${EN_DASH} Thursday: 10:00 AM ${EN_DASH} 9:00 PM`,
  `Friday ${EN_DASH} Saturday: 10:00 AM ${EN_DASH} 9:00 PM`,
  `Sunday: 9:00 AM ${EN_DASH} 10:00 PM`,
];
export const STORE_HOURS_NATURAL_SENTENCE =
  "We're open Monday to Thursday from 10:00 AM to 9:00 PM, Friday to Saturday from 10:00 AM to 9:00 PM, and Sunday from 9:00 AM to 10:00 PM.";

export const getStoreHoursBlock = () =>
  `${STORE_HOURS_TITLE}\n\n${STORE_HOURS_LINES.join('\n')}`;

export const getStoreHoursWithNaturalSentence = () =>
  `${getStoreHoursBlock()}\n\n${STORE_HOURS_NATURAL_SENTENCE}`;

// ===============================
// FORMATTERS
// ===============================

const formatServiceTable = (services) => {
  if (!services.length) return '';

  // Group services by category for a clean, predictable response format.
  const grouped = {};
  services.forEach((s) => {
    const cat = (s.category_name && s.category_name.trim()) || 'Uncategorized';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(s);
  });

  let result = 'Service Menu:\n';
  const sortedCategories = Object.keys(grouped).sort();

  sortedCategories.forEach((category) => {
    const items = grouped[category].slice().sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    );

    result += `\n${category}:\n`;
    items.forEach((s) => {
      const price = s.price ? `${PESO_SYMBOL}${parseFloat(s.price).toFixed(2)}` : 'N/A';
      const duration = s.duration_minutes ? `${s.duration_minutes} min` : 'Flexible';

      // Format: Service Name - Duration - Price
      result += `- ${s.name} ${EN_DASH} ${duration} ${EN_DASH} ${price}\n`;
    });
  });

  return result;
};

// ===============================
// RESPONSE GENERATORS
// ===============================

export const generateServiceListResponse = async () => {
  const services = await fetchServices();

  if (!services.length) {
    return 'Unable to load services right now. Please try again later.';
  }

  return `${formatServiceTable(services)}\n\n${getStoreHoursWithNaturalSentence()}`;
};

export const generatePromosResponse = async () => {
  const promos = await fetchPromos();

  if (!promos.length) {
    return 'No active promotional offers at the moment.';
  }

  let result = '*Current Promotions*\n\n';

  promos.forEach((p, index) => {
    result += `${index + 1}. ${p.promo_name}\n`;
    if (p.description) {
      result += `   ${p.description}\n`;
    }
    result += '\n';
  });

  return result;
};

export const generateBranchesResponse = async () => {
  const branches = await fetchBranches();

  if (!branches.length) {
    return 'Unable to load branches.';
  }

  let result = '*Our Branches*\n\n';

  branches.forEach((b, index) => {
    result += `${index + 1}. ${b.branch_name}\n`;
    result += `   Address: ${b.address}\n\n`;
  });

  return result;
};

export const generateFeaturesResponse = async () => {
  try {
    const features = [];

    // Fetch actual counts from API
    const services = await fetchServices();
    const therapists = await fetchTherapists();
    const branches = await fetchBranches();
    const promos = await fetchPromos();

    // Build dynamic features based on real data
    if (services.length > 0) {
      features.push(`${services.length} Professional Wellness Services`);
    }
    if (therapists.length > 0) {
      features.push(`${therapists.length}+ Expert Therapists`);
    }
    if (branches.length > 0) {
      features.push(`${branches.length} Convenient Branches`);
    }
    if (promos.length > 0) {
      features.push(`${promos.length} Active Promotions`);
    }

    // Add core features
    features.push(
      'Easy Online Booking - Reserve appointments anytime',
      'Digital Receipts - Instant confirmations',
      'Booking History - Track your wellness journey',
      'Smart Notifications - Personalized reminders'
    );

    if (features.length === 0) {
      return 'Hygea offers online booking, professional therapists, multiple locations, and digital receipts.';
    }

    return `*Why Choose Hygea?*\n\n${features.join('\n')}`;
  } catch (err) {
    console.error('Error generating features:', err);
    return '*Why Choose Hygea?*\n\nEasy Online Booking\nProfessional Therapists\nMultiple Locations\nDigital Receipts\nBooking History\nNotifications';
  }
};

export const generateFAQResponse = (input) => {
  const faq = staticChatbotData.faqs.find((f) =>
    input.includes(f.question.toLowerCase())
  );

  if (faq) {
    return `*${faq.question}*\n\n${faq.answer}`;
  }

  let result = '*Frequently Asked Questions*\n\n';
  staticChatbotData.faqs.forEach((f, i) => {
    result += `${i + 1}. ${f.question}\n`;
  });
  result += '\nAsk any of these questions for detailed answers!';

  return result;
};

export const generateNavigationResponse = () => {
  let result = '*Navigation Guide*\n\n';
  staticChatbotData.navigationGuides.forEach((g, i) => {
    result += `${i + 1}. *${g.page}*\n   ${g.description}\n\n`;
  });
  return result;
};
